/**
 * 毎月の定額売上(2026-10-23 段階3「定額」の型)。売上・原価入力の画面から開く(経理・管理者)。
 *  - 上: 登録済みのひな形と、この月の計上状況(計上済み/未計上/対象外)。「◯月分を計上」でまだの分だけ作る
 *  - ひな形の追加・修正は管理者だけ(保存先が設定のため)
 * 計算は fixedRevenue.ts、読み書きは fixedRevenueRepo.ts
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { fetchVendors } from '@core/masters/vendorRepo';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import { toMonthLabel } from '@core/lib/date';
import { isReceivable } from '@core/partners/terms';
import type { DepartmentId, MonthStr, Vendor } from '@core/lib/types';
import { isInPeriod, nextFixedRevenueId, postingDateOf, validateFixedRevenue, type FixedRevenue } from './fixedRevenue';
import { fetchFixedRevenues, fixedRevenueStatus, postFixedRevenues, saveFixedRevenues } from './fixedRevenueRepo';

type Editing = { item: FixedRevenue; amountText: string; isNew: boolean };

const DAY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'end', label: '末日' },
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}日` })),
];

export default function FixedRevenueModal({ month, onClose, onDone }: { month: MonthStr; onClose: () => void; onDone: () => void }) {
  const { user, isAdmin } = useAuth();
  const { accounts, subAccountsOf, departments, companies, defaultCompanyId, fiscal, departmentName, accountName } = useMasters();
  const toast = useToast();
  const [items, setItems] = useState<FixedRevenue[]>([]);
  const [posted, setPosted] = useState<Set<string>>(new Set());
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);

  const vendorName = useCallback((id: string) => vendors.find((v) => v.id === id)?.name ?? id, [vendors]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, vs] = await Promise.all([fetchFixedRevenues(), fetchVendors(true).catch(() => [] as Vendor[])]);
      setItems(list);
      setVendors(vs);
      const status = await fixedRevenueStatus(list, month, (id) => vs.find((v) => v.id === id)?.name ?? id);
      setPosted(new Set(status.filter((s) => s.posted).map((s) => s.item.id)));
    } catch (e) {
      toast(e instanceof Error ? e.message : '定額売上を読み込めませんでした');
    } finally {
      setLoading(false);
    }
  }, [month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const revenueAccounts = useMemo(() => accounts.filter((a) => a.pl_line === 'revenue' && a.active), [accounts]);
  const customers = useMemo(() => vendors.filter((v) => v.active && isReceivable(v)), [vendors]);
  const todo = items.filter((i) => isInPeriod(i, month) && !posted.has(i.id));
  const todoTotal = todo.reduce((s, i) => s + i.amount, 0);

  function stateOf(i: FixedRevenue): { label: string; badge: string } {
    if (!isInPeriod(i, month)) return { label: '対象外', badge: 'b-plan' };
    return posted.has(i.id) ? { label: '計上済み', badge: 'b-ok' } : { label: '未計上', badge: 'b-pend' };
  }

  function openNew() {
    const account = revenueAccounts[0];
    const sub = account ? subAccountsOf(account.id)[0] : undefined;
    setEditing({
      isNew: true,
      amountText: '',
      item: {
        id: nextFixedRevenueId(items),
        name: '',
        company_id: defaultCompanyId,
        department_id: 'honbu' as DepartmentId,
        account_id: account?.id ?? '',
        sub_account_id: sub?.id ?? '',
        vendor_id: null,
        amount: 0,
        day: 'end',
        description: '',
        from_month: month,
        until_month: null,
        active: true,
      },
    });
  }

  async function saveEditing() {
    if (!user || !editing) return;
    const amount = parseAmount(editing.amountText);
    const item: FixedRevenue = { ...editing.item, amount: amount ?? 0, name: editing.item.name.trim(), description: editing.item.description.trim() };
    const invalid = validateFixedRevenue(item);
    if (invalid) {
      toast(invalid);
      return;
    }
    const next = editing.isNew ? [...items, item] : items.map((i) => (i.id === item.id ? item : i));
    setBusy(true);
    try {
      await saveFixedRevenues(next, user.id);
      toast('保存しました');
      setEditing(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function post() {
    if (!user || todo.length === 0) return;
    setBusy(true);
    try {
      const r = await postFixedRevenues({ items, month, vendorName, accounts, userId: user.id, fiscalStartMonth: fiscal.start_month });
      if (r.error) toast(`${r.created}件を計上しました。残りは計上できませんでした: ${r.error}`);
      else toast(`${toMonthLabel(month)}分を${r.created}件計上しました${r.skipped > 0 ? `(計上済み ${r.skipped}件は飛ばしました)` : ''}`);
      onDone();
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '計上に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    const e = editing.item;
    const set = (patch: Partial<FixedRevenue>) => setEditing({ ...editing, item: { ...e, ...patch } });
    return (
      <Modal onClose={() => setEditing(null)} width="min(640px, 94vw)">
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
          <h3>{editing.isNew ? '定額売上を追加' : '定額売上を修正'}</h3>
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <label>名前</label>
            <input value={e.name} onChange={(ev) => set({ name: ev.target.value })} placeholder="例: 事務料(〇〇社)" />
          </div>
          <div>
            <label>会社</label>
            <select value={e.company_id} onChange={(ev) => set({ company_id: ev.target.value })}>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>事業部</label>
            <select value={e.department_id} onChange={(ev) => set({ department_id: ev.target.value as DepartmentId })}>
              {departments
                .filter((d) => d.active !== false)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label>科目①(売上)</label>
            <select
              value={e.account_id}
              onChange={(ev) => set({ account_id: ev.target.value, sub_account_id: subAccountsOf(ev.target.value)[0]?.id ?? '' })}
            >
              {revenueAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>科目②</label>
            <select value={e.sub_account_id} onChange={(ev) => set({ sub_account_id: ev.target.value })}>
              {subAccountsOf(e.account_id).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>取引先(請求先)</label>
            <select value={e.vendor_id ?? ''} onChange={(ev) => set({ vendor_id: ev.target.value || null })}>
              <option value="">なし</option>
              {customers.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}({v.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>金額(税込・毎月)</label>
            <input
              inputMode="numeric"
              value={editing.amountText}
              onChange={(ev) => setEditing({ ...editing, amountText: formatAmountInput(ev.target.value) })}
            />
          </div>
          <div>
            <label>計上日</label>
            <select value={String(e.day)} onChange={(ev) => set({ day: ev.target.value === 'end' ? 'end' : Number(ev.target.value) })}>
              {DAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>開始月</label>
            <input type="month" value={e.from_month ?? ''} onChange={(ev) => set({ from_month: ev.target.value || null })} />
          </div>
          <div>
            <label>終了月(空 = 続ける)</label>
            <input type="month" value={e.until_month ?? ''} onChange={(ev) => set({ until_month: ev.target.value || null })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>摘要(請求書の明細に出ます。空なら名前)</label>
            <input value={e.description} onChange={(ev) => set({ description: ev.target.value })} />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', cursor: 'pointer' }}>
            <input type="checkbox" style={{ margin: 0, width: 15, height: 15 }} checked={e.active} onChange={(ev) => set({ active: ev.target.checked })} />
            使う(外すと計上しない)
          </label>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          金額を直しても、計上済みの月の取引は変わりません(これから計上する月から新しい金額になります)。
        </p>
        <div className="modal-actions">
          <button className="btn-line" onClick={() => setEditing(null)} disabled={busy}>
            戻る
          </button>
          <button className="btn-green" onClick={() => void saveEditing()} disabled={busy}>
            {busy ? '保存中…' : '保存する'}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} width="min(900px, 94vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>毎月の定額売上({toMonthLabel(month)})</h3>
      </div>
      <p className="hint" style={{ marginBottom: 10 }}>
        毎月決まった金額の売上(事務料など)を、ボタンで月ごとに計上します。同じ月に2回押しても二重には計上されません。
      </p>
      {loading ? (
        <p className="empty">読み込み中…</p>
      ) : items.length === 0 ? (
        <p className="empty">定額売上はまだ登録されていません{isAdmin ? '' : '(登録は管理者が行います)'}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>名前</th>
              <th>事業部</th>
              <th>科目</th>
              <th>取引先</th>
              <th className="num">金額</th>
              <th>計上日</th>
              <th>{toMonthLabel(month)}</th>
              {isAdmin && <th />}
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const st = stateOf(i);
              return (
                <tr key={i.id}>
                  <td>
                    {i.name}
                    {!i.active && <span className="hint">(使わない)</span>}
                  </td>
                  <td>{departmentName(i.department_id)}</td>
                  <td>{accountName(i.account_id)}</td>
                  <td>{i.vendor_id ? vendorName(i.vendor_id) : '—'}</td>
                  <td className="num">{formatYen(i.amount)}</td>
                  <td>{isInPeriod(i, month) ? postingDateOf(month, i.day).slice(5).replace('-', '/') : '—'}</td>
                  <td>
                    <span className={`badge ${st.badge}`}>{st.label}</span>
                  </td>
                  {isAdmin && (
                    <td>
                      <button
                        className="txn-link"
                        onClick={() => setEditing({ item: i, amountText: formatAmountInput(String(i.amount)), isNew: false })}
                      >
                        修正
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="modal-actions">
        {isAdmin && (
          <button className="btn-line" onClick={openNew} disabled={busy || loading}>
            追加する
          </button>
        )}
        <button className="btn-green" onClick={() => void post()} disabled={busy || loading || todo.length === 0}>
          {busy ? '計上中…' : todo.length === 0 ? `${toMonthLabel(month)}分は計上済み` : `${toMonthLabel(month)}分を計上(${todo.length}件・¥${formatYen(todoTotal)})`}
        </button>
      </div>
    </Modal>
  );
}
